/**
 * UpgradeChoiceUI - The upgrade choice display component.
 *
 * Responsibilities:
 * - Show available upgrade branches for a selected battlefield minion
 * - Display each branch's stats/effects (HP change, Attack change, abilities)
 * - Display the upgrade cost (from game config)
 * - React to backend state updates (GameStateUpdate, etc.)
 * - Provide reactive render data for UI binding
 * - Submit upgrade choice to the authoritative backend
 *
 * The UpgradeChoiceUI reads state from AppShell (which holds the authoritative
 * GameStateService) and computes display data for the upgrade choice overlay.
 * The backend is authoritative; upgrade branches are computed from shared
 * upgrade path definitions and validated by the server.
 */

import { AppShell, AppShellEvent } from '../AppShell';
import { MinionOnBoardData } from '../../shared/messages/MessageContracts';
import { UpgradeBranch, getEligibleAbilityUpgrades, resolveAbilityNames, formatAbilityNames, abilitiesReplacedByUpgrade, HIGH_STAKES_BRANCH_ID, type AbilityDefinitionSummary } from '../../shared/models/UpgradePath';
import { RUNE_HIGH_STAKES_ID, HIGH_STAKES_STAT_BONUS, highStakesDestroyChance } from '../../shared/models/RuneDefinitions';
import { GameConfig } from '../../config/gameConfig';
import { GRANDMASTER_ABILITY_ID, ITEM_MINION_TYPE } from '../../shared/models/AbilityConstants';
import { GAME_ABILITY_DEFINITIONS } from '../../services/GameAbilities';
import { MINION_TYPE_NAMES } from '../../shared/models/MinionType';
import { getTransformFor } from '../../shared/models/TransformDefinitions';

/**
 * Lightweight summary of ability definitions for upgrade eligibility checks.
 * Derived from GAME_ABILITY_DEFINITIONS at compile time.
 */
const ABILITY_SUMMARIES: AbilityDefinitionSummary[] = GAME_ABILITY_DEFINITIONS.map(
  (def) => ({ id: def.id, name: def.name, description: def.description, types: def.types, availableAtLevel: def.availableAtLevel })
);

// ─── Upgrade Branch Render Data ─────────────────────────────────────────────

/**
 * Render data for a single upgrade branch option.
 */
export interface UpgradeBranchRenderData {
  /** Unique identifier for this upgrade branch (e.g., "hp", "atk", "ability"). */
  branchId: string;

  /** Human-readable name of the upgrade. */
  name: string;

  /** HP change applied when this upgrade is chosen. */
  hpChange: number;

  /** Attack change applied when this upgrade is chosen. */
  attackChange: number;

  /** Ability IDs added by this upgrade (empty array if none). */
  abilitiesAdded: string[];

  /** Ability IDs removed by this upgrade (empty array if none). */
  abilitiesRemoved: string[];

  /** Whether this branch adds any abilities. */
  hasAbilitiesAdded: boolean;

  /** Whether this branch removes any abilities. */
  hasAbilitiesRemoved: boolean;

  /** The resulting HP after applying this upgrade (currentHp + hpChange). */
  resultingHp: number;

  /** The resulting attack after applying this upgrade (attack + attackChange). */
  resultingAttack: number;

  /** For ability branches: names of eligible abilities that could be granted. */
  eligibleAbilityNames: string[];

  /**
   * For ability branches: the granted ability's description (the shared
   * GAME_ABILITY_DEFINITIONS text, resolved from the server-provided
   * abilityId). The panel shows keyword + description instead of the
   * HP/ATK stat lines for these branches. Empty when no specific ability
   * is known yet (the transient local "Gain an ability…" fallback) or the
   * branch grants no ability.
   */
  abilityDescription: string;

  /**
   * For 'transform' branches ONLY: the class the transform turns the minion
   * into (e.g. 'runekeeper'). The panel renders these branches as a
   * pulsing yellow PORTAL carrying the class name (no name/stat lines).
   * Absent for every other branch.
   */
  transformTargetClass?: string;

  /**
   * For the Rune of High Stakes branch ('high_stakes') ONLY: the chance
   * (0..1) that choosing the option DESTROYS the minion instead of
   * applying the +10/+10 (10% × level — the shared highStakesDestroyChance).
   * The panel renders it as a red warning line. Absent for every other
   * branch.
   */
  destroyChance?: number;
}

/**
 * Render data for the selected minion being upgraded.
 */
export interface SelectedMinionRenderData {
  /** Unique instance ID for this minion copy. */
  instanceId: string;

  /** The base minion definition ID. */
  baseMinionId: string;

  /** Current HP of the minion. */
  currentHp: number;

  /** Attack value of the minion. */
  attack: number;

  /** Number of upgrades applied (0 = base). */
  upgradeCount: number;

  /** Computed level: upgradeCount + 1. */
  level: number;

  /** Ability IDs present on this minion. */
  abilities: string[];

  /** Current upgrade path state identifier. */
  upgradePathState: string;

  /** Position on the battlefield (0-indexed, left to right). */
  battlefieldPosition: number;

  /** Whether this minion has reached the maximum upgrade count. */
  isMaxLevel: boolean;
}

/**
 * Render data for the entire upgrade choice UI.
 */
export interface UpgradeChoiceUIRenderData {
  /** Whether the upgrade choice UI should be visible. */
  isVisible: boolean;

  /** Whether a minion is currently selected for upgrade. */
  hasSelectedMinion: boolean;

  /** The selected minion's data (null if none selected). */
  selectedMinion: SelectedMinionRenderData | null;

  /** Available upgrade branches for the selected minion. */
  availableBranches: UpgradeBranchRenderData[];

  /** Number of available branches. */
  branchCount: number;

  /** Whether the selected minion has any upgrades available. */
  hasAvailableUpgrades: boolean;

  /** Whether the selected minion is at max level (no further upgrades). */
  isMaxLevel: boolean;

  /** The cost in gold for performing an upgrade. */
  upgradeCost: number;

  /** The current player's available gold. */
  playerGold: number;

  /** Whether the player has enough gold for an upgrade. */
  canAffordUpgrade: boolean;

  /** Whether the player is in preparation phase (upgrades only allowed then). */
  isInPreparationPhase: boolean;
}

/**
 * Callback invoked when the render data changes.
 */
export type UpgradeChoiceUIRenderCallback = (data: UpgradeChoiceUIRenderData) => void;

/**
 * Callback invoked when an upgrade branch is selected.
 */
export type UpgradeBranchSelectedCallback = (
  minionInstanceId: string,
  branchId: string
) => void;

// ─── UpgradeChoiceUI ────────────────────────────────────────────────────────

export class UpgradeChoiceUI {
  private _appShell: AppShell;
  private _playerId: string;
  private _renderCallbacks: UpgradeChoiceUIRenderCallback[] = [];
  private _branchSelectedCallbacks: UpgradeBranchSelectedCallback[] = [];
  private _eventUnsubscribe: (() => void) | null = null;
  private _isVisible: boolean = false;
  private _selectedMinionInstanceId: string | null = null;

  constructor(appShell: AppShell) {
    this._appShell = appShell;
    this._playerId = appShell.playerId;
  }

  // ─── Public Accessors ────────────────────────────────────────────────────

  /** Returns the current render data (computed fresh from AppShell state). */
  get renderData(): UpgradeChoiceUIRenderData {
    return this._buildRenderData();
  }

  /** Returns true if the upgrade choice UI is currently visible/active. */
  get isVisible(): boolean {
    return this._isVisible;
  }

  /** Returns the currently selected minion instance ID, or null. */
  get selectedMinionInstanceId(): string | null {
    return this._selectedMinionInstanceId;
  }

  // ─── Lifecycle ───────────────────────────────────────────────────────────

  /**
   * Show the upgrade choice UI and start listening for state changes.
   */
  show(): void {
    if (this._isVisible) {
      return;
    }

    this._isVisible = true;

    // Listen for app shell events to trigger render updates
    this._eventUnsubscribe = this._appShell.onEvent(
      this._onAppEvent.bind(this)
    );

    // Initial render
    this._notifyRenderCallbacks();
  }

  /**
   * Hide the upgrade choice UI and stop listening for state changes.
   */
  hide(): void {
    if (!this._isVisible) {
      return;
    }

    this._isVisible = false;
    this._selectedMinionInstanceId = null;

    if (this._eventUnsubscribe) {
      this._eventUnsubscribe();
      this._eventUnsubscribe = null;
    }
  }

  // ─── Render Callbacks ────────────────────────────────────────────────────

  /**
   * Subscribe to render data changes.
   * Returns an unsubscribe function.
   */
  onRenderChange(callback: UpgradeChoiceUIRenderCallback): () => void {
    this._renderCallbacks.push(callback);
    return () => {
      const index = this._renderCallbacks.indexOf(callback);
      if (index !== -1) {
        this._renderCallbacks.splice(index, 1);
      }
    };
  }

  /**
   * Subscribe to branch selection events.
   * Returns an unsubscribe function.
   */
  onBranchSelected(callback: UpgradeBranchSelectedCallback): () => void {
    this._branchSelectedCallbacks.push(callback);
    return () => {
      const index = this._branchSelectedCallbacks.indexOf(callback);
      if (index !== -1) {
        this._branchSelectedCallbacks.splice(index, 1);
      }
    };
  }

  // ─── Player Actions ──────────────────────────────────────────────────────

  /**
   * Select a minion for upgrade by its instance ID.
   *
   * Validates that the minion exists on the current battlefield and belongs
   * to the current player. Requests upgrade options from the server (which
   * pre-selects the random ability). Updates the selected minion and triggers re-render.
   *
   * @param minionInstanceId - The instance ID of the minion to select.
   * @returns true if the minion was selected successfully, false otherwise.
   */
  selectMinion(minionInstanceId: string): boolean {
    // Validate: must be in a match
    if (!this._appShell.isInMatch) {
      return false;
    }

    // Validate: minion must exist on current player's battlefield
    const minion = this._findMinionOnBattlefield(minionInstanceId);
    if (!minion) {
      return false;
    }

    this._selectedMinionInstanceId = minionInstanceId;

    // Request upgrade options from server (pre-selects the random ability)
    const matchId = this._appShell.gameState.match.matchId;
    if (matchId) {
      this._appShell.getUpgradeOptions(matchId, minionInstanceId);
    }

    this._notifyRenderCallbacks();
    return true;
  }

  /**
   * Deselect the currently selected minion.
   */
  deselectMinion(): void {
    this._selectedMinionInstanceId = null;
    this._notifyRenderCallbacks();
  }

  /**
   * Select an upgrade branch and submit the upgrade action to the backend.
   *
   * Validates that a minion is selected, the branch is available, and the
   * player has sufficient gold. Sends an UpgradeMinionRequest to the backend
   * through AppShell. The backend is authoritative — it validates the upgrade
   * and produces the resulting state. The UI reconciles when the GameStateUpdate
   * arrives.
   *
   * @param branchId - The ID of the upgrade branch to apply.
   * @returns true if the upgrade request was sent successfully, false otherwise.
   */
  chooseUpgrade(branchId: string): boolean {
    if (!this._selectedMinionInstanceId) {
      return false;
    }

    // Validate: must be in a match
    if (!this._appShell.isInMatch) {
      return false;
    }

    // Validate: must be in preparation phase (reject during combat/locked)
    const matchState = this._appShell.gameState.match;
    const allPlayers = matchState.players;
    const currentPlayer = allPlayers.find((p) => p.id === this._playerId) ?? null;
    const playerPhase = currentPlayer?.phase ?? matchState.phase;
    if (playerPhase !== 'preparation') {
      return false;
    }

    // Validate: minion must still be on battlefield
    const minion = this._findMinionOnBattlefield(this._selectedMinionInstanceId);
    if (!minion) {
      return false;
    }

    // Validate: branchId must be a known type (hp, atk, ability, the Rune
    // of Options 4th option 'bonus', 'transform', or the Rune of High
    // Stakes option 'high_stakes')
    if (!['hp', 'atk', 'ability', 'bonus', 'transform', HIGH_STAKES_BRANCH_ID].includes(branchId)) {
      return false;
    }

    // Get match ID from current state
    const matchId = this._appShell.gameState.match.matchId;
    if (!matchId) {
      return false;
    }

    // Extract the selectedAbilityId from the server's UpgradeOptionsResponse
    // so the server applies exactly the ability the player was shown (the
    // 'ability' branch AND the Rune of Options 'bonus' branch when it
    // carries an ability — the stat fallback has an empty abilityId).
    let selectedAbilityId: string | undefined;
    if ((branchId === 'ability' || branchId === 'bonus') &&
        matchState.upgradeOptions &&
        matchState.upgradeOptionsMinionId === this._selectedMinionInstanceId) {
      const abilityOption = matchState.upgradeOptions.find(
        (opt) => opt.branchId === branchId
      );
      if (abilityOption?.abilityId) {
        selectedAbilityId = abilityOption.abilityId;
      }
    }

    // Notify branch-selected callbacks
    this._notifyBranchSelectedCallbacks(this._selectedMinionInstanceId, branchId);

    // Send upgrade request through AppShell (which sends to backend via WebSocket)
    const success = this._appShell.upgradeMinion(
      matchId,
      this._selectedMinionInstanceId,
      branchId,
      selectedAbilityId
    );

    // Deselect after submitting (UI will update when GameStateUpdate arrives)
    if (success) {
      this._selectedMinionInstanceId = null;
      this._notifyRenderCallbacks();
    }

    return success;
  }

  // ─── Internal Methods ────────────────────────────────────────────────────

  private _onAppEvent(event: AppShellEvent): void {
    switch (event.type) {
      case 'stateChange': {
        this._notifyRenderCallbacks();
        break;
      }
      case 'screenChange': {
        const data = event.data as { previousScreen: string; currentScreen: string };
        // Hide when navigating away from match screen
        if (data.currentScreen !== 'match' && this._isVisible) {
          this.hide();
        }
        break;
      }
    }
  }

  private _notifyRenderCallbacks(): void {
    const data = this._buildRenderData();
    for (const callback of this._renderCallbacks) {
      callback(data);
    }
  }

  private _notifyBranchSelectedCallbacks(
    minionInstanceId: string,
    branchId: string
  ): void {
    for (const callback of this._branchSelectedCallbacks) {
      callback(minionInstanceId, branchId);
    }
  }

  private _buildRenderData(): UpgradeChoiceUIRenderData {
    const matchState = this._appShell.gameState.match;
    const allPlayers = matchState.players;

    // Find current player's data
    const currentPlayer = allPlayers.find((p) => p.id === this._playerId) ?? null;

    const playerGold = currentPlayer?.gold ?? 0;
    const playerPhase = currentPlayer?.phase ?? matchState.phase;
    const isInPreparationPhase = playerPhase === 'preparation';

    // Find selected minion
    let selectedMinion: SelectedMinionRenderData | null = null;
    let availableBranches: UpgradeBranchRenderData[] = [];
    let hasAvailableUpgrades = false;
    let isMaxLevel = false;

    if (this._selectedMinionInstanceId) {
      const minionData = this._findMinionOnBattlefield(
        this._selectedMinionInstanceId
      );

      if (minionData) {
        selectedMinion = this._buildSelectedMinionData(minionData);
        isMaxLevel = selectedMinion.upgradeCount >= this.currentMaxUpgrades();

        if (!isMaxLevel) {
          availableBranches = this._buildBranchRenderData(minionData);
          hasAvailableUpgrades = availableBranches.length > 0;
        }
      }
    }

    // Upgrade cost is per-minion: base (3) + minion's upgradeCount
    // e.g., level 1→2 costs 3, level 2→3 costs 4, etc.
    const minionUpgradeCount = selectedMinion?.upgradeCount ?? 0;
    const upgradeCost = GameConfig.upgradeCost + minionUpgradeCount;
    const canAffordUpgrade = playerGold >= upgradeCost;

    return {
      isVisible: this._isVisible && this._appShell.isInMatch,
      hasSelectedMinion: selectedMinion !== null,
      selectedMinion,
      availableBranches,
      branchCount: availableBranches.length,
      hasAvailableUpgrades,
      isMaxLevel,
      upgradeCost,
      playerGold,
      canAffordUpgrade,
      isInPreparationPhase,
    };
  }

  private _findMinionOnBattlefield(
    instanceId: string
  ): MinionOnBoardData | null {
    const matchState = this._appShell.gameState.match;
    const allPlayers = matchState.players;
    const currentPlayer = allPlayers.find((p) => p.id === this._playerId) ?? null;

    if (!currentPlayer) {
      return null;
    }

    const minion =
      currentPlayer.battlefield.find((m) => m.instanceId === instanceId) ?? null;
    return minion;
  }

  /**
   * The effective max upgrade count for the current player — the base cap
   * plus ONE per Grandmaster minion on the player's OWN battlefield
   * (Grandmaster extends the level cap while it is on the board; the
   * effects stack). Display-only mirror of the authoritative
   * PlayerState.getMaxUpgrades — the server validates every upgrade.
   *
   * Public so the battlefield click handler (main.ts) can gate upgrade
   * selection on the SAME Grandmaster-aware cap the UI mirror uses (a
   * hardcoded base cap here would block selecting max-level minions while
   * a Grandmaster is on the board).
   */
  public currentMaxUpgrades(): number {
    const matchState = this._appShell.gameState.match;
    const currentPlayer = matchState?.players?.find((p) => p.id === this._playerId) ?? null;
    const grandmasterCount = (currentPlayer?.battlefield ?? [])
      .filter((m) => (m.abilities ?? []).includes(GRANDMASTER_ABILITY_ID))
      .length;
    return GameConfig.maxUpgrades + grandmasterCount;
  }

  private _buildSelectedMinionData(
    minionData: MinionOnBoardData
  ): SelectedMinionRenderData {
    const upgradeCount = minionData.upgradeCount;
    const level = upgradeCount + 1;
    const isMaxLevel = upgradeCount >= this.currentMaxUpgrades();

    return {
      instanceId: minionData.instanceId,
      baseMinionId: minionData.baseMinionId,
      currentHp: minionData.currentHp,
      attack: minionData.attack,
      upgradeCount,
      level,
      abilities: [...minionData.abilities], // Defensive copy
      upgradePathState: minionData.upgradePathState,
      battlefieldPosition: minionData.battlefieldPosition,
      isMaxLevel,
    };
  }

  /**
   * Builds available upgrade branches from server-provided options.
   * Falls back to local computation if server options aren't available yet.
   */
  private _buildBranchRenderData(
    minionData: MinionOnBoardData
  ): UpgradeBranchRenderData[] {
    // ITEMS can never be upgraded (the class rule — they are always level
    // 1). The server offers no options for them and rejects every branch,
    // so render NO branches — never the local fallback (which would show
    // phantom +5/+5 buttons). The panel never opens (it requires at least
    // one branch), matching the authoritative backend.
    if (minionData.minionType === ITEM_MINION_TYPE) return [];

    // Use server-provided options if available for this minion
    const matchState = this._appShell.gameState.match;
    if (matchState.upgradeOptions &&
        matchState.upgradeOptionsMinionId === minionData.instanceId &&
        matchState.upgradeOptions.length > 0) {
      return matchState.upgradeOptions.map((opt) => {
        // For ability options (the 'ability' branch AND the Rune of Options
        // 'bonus' branch when it carries an ability), mirror the server's
        // branch shape: abilities the new one REPLACES (Greater Heal removes
        // Heal) travel with the addition (abilitiesReplacedByUpgrade is the
        // shared rule).
        const isAbilityOption =
          (opt.branchId === 'ability' || opt.branchId === 'bonus') && opt.abilityId.length > 0;
        const abilitiesAdded = isAbilityOption ? [opt.abilityId] : [];
        const abilitiesRemoved = abilitiesAdded.length > 0
          ? abilitiesAdded.flatMap((id) => abilitiesReplacedByUpgrade(id))
          : [];
        return {
          branchId: opt.branchId,
          name: opt.name,
          hpChange: opt.hpChange,
          attackChange: opt.attackChange,
          abilitiesAdded,
          abilitiesRemoved,
          hasAbilitiesAdded: isAbilityOption,
          hasAbilitiesRemoved: abilitiesRemoved.length > 0,
          resultingHp: minionData.currentHp + opt.hpChange,
          resultingAttack: minionData.attack + opt.attackChange,
          eligibleAbilityNames: isAbilityOption ? [opt.abilityName] : [],
          // The panel shows the ability's keyword (name) + description;
          // the description is static shared content, resolved here from
          // the same single source the eligibility checks use.
          abilityDescription: isAbilityOption
            ? (ABILITY_SUMMARIES.find((s) => s.id === opt.abilityId)?.description ?? '')
            : '',
          // Transform option: the target class the portal displays (the
          // server's spec — never computed locally).
          transformTargetClass: opt.transformTargetClass ?? undefined,
          // High Stakes option: the destroy chance the server displayed
          // (the authoritative roll uses the same shared formula).
          destroyChance: opt.destroyChance ?? undefined,
        };
      });
    }

    // Fallback: compute locally (shown briefly before server responds)
    const hpBranch: UpgradeBranch = {
      id: 'hp',
      name: 'Health Boost',
      hpChange: 5,
      attackChange: 0,
      nextPathState: `${minionData.upgradePathState}_hp`,
    };
    const atkBranch: UpgradeBranch = {
      id: 'atk',
      name: 'Attack Boost',
      hpChange: 0,
      attackChange: 5,
      nextPathState: `${minionData.upgradePathState}_atk`,
    };

    // Board context for the Second Chance rule: the player's friendly
    // Grandmasters on the OWN battlefield (display-only mirror — the server
    // validates every upgrade with the authoritative count).
    const matchStateForContext = this._appShell.gameState.match;
    const currentPlayer = matchStateForContext?.players?.find((p) => p.id === this._playerId) ?? null;
    const grandmasterCount = (currentPlayer?.battlefield ?? [])
      .filter((m) => (m.abilities ?? []).includes(GRANDMASTER_ABILITY_ID))
      .length;

    const eligible = getEligibleAbilityUpgrades(
      minionData.minionType,
      minionData.abilities,
      minionData.upgradeCount + 1,
      ABILITY_SUMMARIES,
      { grandmasterCount }
    );
    const eligibleAbilityNames = eligible.map((a) => a.name);

    // RUNE OF HIGH STAKES (the shared spec — the server offers the SAME
    // option and re-validates it): while the player owns the rune, the two
    // stat options (+5 HP / +5 ATK) are replaced by a single High Stakes
    // option (+10/+10 with the level-scaled destroy chance). The client
    // reads the authoritative purchased-runes list from the last
    // broadcast, so the fallback panel matches the server's offering even
    // before the response arrives.
    const ownedRunes = currentPlayer?.runes?.purchased ?? [];
    const branches: (UpgradeBranch & { eligibleAbilityNames?: string[]; destroyChance?: number })[] =
      ownedRunes.includes(RUNE_HIGH_STAKES_ID)
        ? [{
            id: HIGH_STAKES_BRANCH_ID,
            name: 'High Stakes',
            hpChange: HIGH_STAKES_STAT_BONUS,
            attackChange: HIGH_STAKES_STAT_BONUS,
            nextPathState: `${minionData.upgradePathState}_${HIGH_STAKES_BRANCH_ID}`,
            destroyChance: highStakesDestroyChance(minionData.upgradeCount + 1),
          }]
        : [hpBranch, atkBranch];
    if (eligible.length > 0) {
      branches.push({
        id: 'ability',
        name: 'Gain an ability…',
        hpChange: 0,
        attackChange: 0,
        abilitiesAdded: [],
        nextPathState: `${minionData.upgradePathState}_ability`,
        eligibleAbilityNames,
      });
    }

    // Transform (the shared TRANSFORM_DEFINITIONS spec — the server offers
    // the SAME option and re-validates it): offered ONLY at the spec's
    // EXACT level for the minion's class. Rendered as a pulsing portal; the
    // branch carries the target class for the portal's label.
    const transform = getTransformFor(minionData.minionType, minionData.upgradeCount + 1);
    if (transform) {
      branches.push({
        id: 'transform',
        name: `Transform into ${MINION_TYPE_NAMES[transform.targetClass]}`,
        hpChange: 0,
        attackChange: 0,
        abilitiesAdded: [],
        nextPathState: `${minionData.upgradePathState}_transform`,
        transformTargetClass: transform.targetClass,
      });
    }

    return branches.map((branch) => {
      const abilitiesAdded = branch.abilitiesAdded ?? [];
      const abilitiesRemoved = branch.abilitiesRemoved ?? [];

      return {
        branchId: branch.id,
        name: branch.name,
        hpChange: branch.hpChange,
        attackChange: branch.attackChange,
        abilitiesAdded,
        abilitiesRemoved,
        hasAbilitiesAdded: abilitiesAdded.length > 0 || branch.id === 'ability',
        hasAbilitiesRemoved: abilitiesRemoved.length > 0,
        resultingHp: minionData.currentHp + branch.hpChange,
        resultingAttack: minionData.attack + branch.attackChange,
        eligibleAbilityNames: branch.eligibleAbilityNames ?? [],
        // Local fallback: no specific ability is pre-selected yet, so there
        // is no description to show (the panel waits for the server response
        // before rendering an ability branch anyway).
        abilityDescription: '',
        // The transform option is spec-derived (no server round-trip
        // needed), so the portal is correct even in the fallback render.
        transformTargetClass: branch.transformTargetClass ?? undefined,
        // The High Stakes option is spec-derived too (shared formula), so
        // the warning is correct even in the fallback render.
        destroyChance: branch.destroyChance ?? undefined,
      };
    });
  }
}
